/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
    });

    this.pendingQuantities = new Map();
    this.quantityRequests = Promise.resolve();
    this.addEventListener('click', this.handleQuantityClick.bind(this), true);
    this.addEventListener('change', this.handleQuantityInput.bind(this), true);
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }

  /**
   * Handle the +, - and Remove controls of a line item
   * @param {object} evt - Event object.
   */
  handleQuantityClick(evt) {
    const control = evt.target.closest('.cart-item__remove, .quantity-down, .quantity-up');
    if (!control) return;

    evt.preventDefault();
    evt.stopPropagation();

    const input = control.closest('.cart-item').querySelector('.cart-item__quantity-input');
    let quantity = parseInt(input.value, 10);

    if (control.classList.contains('cart-item__remove')) {
      quantity = 0;
    } else if (control.classList.contains('quantity-down')) {
      quantity -= 1;
    } else {
      quantity += 1;
    }

    this.queueQuantityChange(input, quantity);
  }

  /**
   * Handle a quantity typed into a line item
   * @param {object} evt - Event object.
   */
  handleQuantityInput(evt) {
    if (!evt.target.matches('.cart-item__quantity-input')) return;

    evt.stopPropagation();
    this.queueQuantityChange(evt.target, parseInt(evt.target.value, 10));
  }

  /**
   * Set a line item's new quantity and send it once the customer stops clicking
   * @param {Element} input - The line item's quantity input.
   * @param {number} requested - Requested quantity.
   */
  queueQuantityChange(input, requested) {
    if (Number.isNaN(requested)) {
      input.value = input.dataset.initialValue;
      return;
    }

    let quantity = Math.max(requested, 0);
    const max = parseInt(input.max, 10);

    if (max && quantity > max) {
      quantity = max;
      theme.showQuickPopup(theme.strings.cartItemsQuantityError.replace('[QUANTITY]', max), input);
    }

    input.value = quantity;
    this.pendingQuantities.set(input.dataset.key, quantity);

    clearTimeout(this.quantityTimeout);
    this.quantityTimeout = setTimeout(() => {
      const changes = Array.from(this.pendingQuantities);
      this.pendingQuantities.clear();
      changes.forEach(([key]) => {
        this.querySelector(`.cart-item__quantity-input[data-key="${key}"]`)
          ?.closest('[data-merge-list-item]')
          ?.classList.add('merge-item-refreshing');
      });
      this.quantityRequests = this.quantityRequests.then(() => this.sendQuantityChanges(changes));
    }, quantity === 0 ? 10 : 700);
  }

  /**
   * Send line item quantities one line at a time, then refresh the cart
   * @param {Array} changes - [line item key, quantity] pairs.
   */
  async sendQuantityChanges(changes) {
    try {
      for (const [id, quantity] of changes) {
        const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ id, quantity })
        });

        if (!response || !response.ok) {
          throw new Error(`HTTP error! Status: ${response ? response.status : 'none'}`);
        }
      }
    } catch (error) {
      this.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: {
          error: error.message
        }
      }));
    }

    document.dispatchEvent(
      new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
    );
  }
}

window.customElements.define('cart-drawer', CartDrawer);
